import { useSyncExternalStore } from 'react';

export type PwaUpdatePhase = 'idle' | 'updating' | 'ready' | 'error';
export type PwaStatus = Readonly<{ updateAvailable: boolean; phase: PwaUpdatePhase }>;
export type PwaUpdateRuntime = Readonly<{
  pathname: () => string;
  online: () => boolean;
  reload: () => void;
  setTimer: (callback: () => void, delay: number) => number;
  clearTimer: (timer: number) => void;
}>;

type ApplyWaitingUpdate = (reloadPage?: boolean) => Promise<void>;
const activationTimeoutMs = 15_000;

export function isSafePwaUpdatePath(pathname: string) {
  return !pathname.startsWith('/games/');
}

export function createPwaUpdateController(runtime: PwaUpdateRuntime) {
  const listeners = new Set<() => void>();
  let status: PwaStatus = { updateAvailable: false, phase: 'idle' };
  let applyWaitingUpdate: ApplyWaitingUpdate | null = null;
  let activationTimer: number | null = null;
  let reloadRequested = false;

  const publish = (next: Partial<PwaStatus>) => {
    status = { ...status, ...next };
    listeners.forEach((listener) => listener());
  };
  const clearActivationTimer = () => {
    if (activationTimer === null) return;
    runtime.clearTimer(activationTimer);
    activationTimer = null;
  };
  const reloadAtSafePoint = () => {
    if (reloadRequested || !isSafePwaUpdatePath(runtime.pathname())) return false;
    reloadRequested = true;
    publish({ updateAvailable: false, phase: 'idle' });
    runtime.reload();
    return true;
  };

  return {
    getStatus: () => status,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setApplyWaitingUpdate(callback: ApplyWaitingUpdate) {
      applyWaitingUpdate = callback;
    },
    needRefresh() {
      reloadRequested = false;
      clearActivationTimer();
      publish({ updateAvailable: true, phase: 'idle' });
    },
    needReload() {
      clearActivationTimer();
      publish({ updateAvailable: true, phase: 'ready' });
      reloadAtSafePoint();
    },
    finishDeferredReload: reloadAtSafePoint,
    registrationError() {
      clearActivationTimer();
      if (status.updateAvailable) publish({ phase: 'error' });
    },
    online() {
      if (status.updateAvailable && status.phase === 'error') publish({ phase: 'idle' });
    },
    async activate() {
      if (!isSafePwaUpdatePath(runtime.pathname()) || !applyWaitingUpdate || status.phase === 'updating' || status.phase === 'ready') return false;
      if (!runtime.online()) {
        publish({ phase: 'error' });
        return false;
      }
      publish({ phase: 'updating' });
      activationTimer = runtime.setTimer(() => {
        activationTimer = null;
        if (status.phase === 'updating') publish({ phase: 'error' });
      }, activationTimeoutMs);
      try {
        await applyWaitingUpdate(true);
        return true;
      } catch (error) {
        console.warn('[pwa] Waiting service worker activation failed', error);
        clearActivationTimer();
        publish({ phase: 'error' });
        return false;
      }
    },
  };
}

const controller = createPwaUpdateController({
  pathname: () => window.location.pathname,
  online: () => navigator.onLine,
  reload: () => window.location.reload(),
  setTimer: (callback, delay) => window.setTimeout(callback, delay),
  clearTimer: (timer) => window.clearTimeout(timer),
});
let registrationStarted = false;

export function usePwaStatus() {
  return useSyncExternalStore(controller.subscribe, controller.getStatus, controller.getStatus);
}

export function activatePwaUpdate() {
  return controller.activate();
}

export function finishDeferredPwaReload() {
  return controller.finishDeferredReload();
}

export function registerPwaUpdates() {
  if (registrationStarted || !('serviceWorker' in navigator)) return;
  registrationStarted = true;
  window.addEventListener('online', controller.online);
  void import('virtual:pwa-register').then(({ registerSW }) => {
    controller.setApplyWaitingUpdate(registerSW({
      immediate: true,
      onNeedRefresh: controller.needRefresh,
      onNeedReload: controller.needReload,
      onRegisteredSW: (_url, registration) => {
        if (!registration) return;
        window.setInterval(() => { void registration.update(); }, 60 * 60 * 1000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void registration.update();
        });
      },
      onRegisterError: (error) => {
        controller.registrationError();
        console.warn('[pwa] Service worker registration failed', error);
      },
    }));
  }).catch((error: unknown) => {
    controller.registrationError();
    console.warn('[pwa] Registration module unavailable', error);
  });
}
